import type { OrderRecord, Position } from "@/api/types";
import { orderReason } from "@/lib/order-labels";

/** One trade: an entry and the exit that closed it (or still open). */
export type RoundTrip = {
  id: string;
  accountId: string;
  backtestId: string | null;
  symbol: string;
  side: "long" | "short";
  qty: number;
  /** Null when the entry is older than the orders that were loaded. */
  entryPrice: number | null;
  entryTime: string | null;
  exitPrice: number | null;
  exitTime: string | null;
  /** Realized result without fees; null when a price is missing. */
  pnl: number | null;
  pnlPct: number | null;
  /** How it ended: Take profit, Stop loss, or the script's own tag. */
  exitReason: string;
  open: boolean;
  /** The order that closed it; null while open. */
  exitOrderId: string | null;
};

type Lot = { qty: number; price: number | null; time: string; order: OrderRecord };

const n = (v: string | null | undefined) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

/** The price an order actually filled at; null when the exchange reported none. */
export function fillPrice(o: OrderRecord): number | null {
  const qty = n(o.executed_qty);
  const price = n(o.executed_price);
  if (price > 0) return price;
  const quote = n(o.quote_spent);
  if (quote > 0 && qty > 0) return quote / qty;
  const asked = n(o.requested_price);
  return asked > 0 ? asked : null;
}

/**
 * Match filled orders into trades, first in first out, per account, symbol and
 * position side: the same rule the server uses for its totals. Only the orders
 * given are known, so an exit whose entry was not loaded has no entry price.
 * Newest first.
 */
export function matchRoundTrips(orders: OrderRecord[]): RoundTrip[] {
  const filled = orders
    .filter((o) => o.status === "done" && n(o.executed_qty) > 0)
    .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));
  const books = new Map<string, { long: Lot[]; short: Lot[] }>();
  const trips: RoundTrip[] = [];
  let seq = 0;

  const push = (o: OrderRecord, side: "long" | "short", qty: number, lot: Lot | null, exit: OrderRecord | null) => {
    const entryPrice = lot ? lot.price : null;
    const exitPrice = exit ? fillPrice(exit) : null;
    const pnl =
      entryPrice != null && exitPrice != null ? (exitPrice - entryPrice) * qty * (side === "long" ? 1 : -1) : null;
    trips.push({
      id: `${o.id}-${seq++}`,
      accountId: o.account_id,
      backtestId: (lot?.order.backtest_id || exit?.backtest_id || o.backtest_id) ?? null,
      symbol: o.symbol,
      side,
      qty,
      entryPrice,
      entryTime: lot ? lot.time : null,
      exitPrice,
      exitTime: exit ? exit.created_at : null,
      pnl,
      pnlPct: pnl != null && entryPrice ? (pnl / (entryPrice * qty)) * 100 : null,
      exitReason: exit ? orderReason(exit.comment) : "",
      open: !exit,
      exitOrderId: exit ? exit.id : null,
    });
  };

  for (const o of filled) {
    const pos = (o.position_side || "").toUpperCase();
    const key = `${o.account_id}|${o.symbol}`;
    let book = books.get(key);
    if (!book) books.set(key, (book = { long: [], short: [] }));
    const buy = o.side === "BUY";
    let qty = n(o.executed_qty);

    // Hedge mode says which side; one-way mode (or spot) closes the opposite side first.
    const closes: "long" | "short" | null =
      pos === "LONG" ? (buy ? null : "long") : pos === "SHORT" ? (buy ? "short" : null) : buy ? "short" : "long";
    if (closes) {
      const lots = book[closes];
      while (qty > 1e-12 && lots.length) {
        const lot = lots[0]!;
        const take = Math.min(lot.qty, qty);
        push(o, closes, take, lot, o);
        lot.qty -= take;
        qty -= take;
        if (lot.qty <= 1e-12) lots.shift();
      }
      if (qty > 1e-12 && (pos === "LONG" || pos === "SHORT")) {
        // An exit with nothing to close: its entry is older than what was loaded.
        push(o, closes, qty, null, o);
        qty = 0;
      }
    }
    if (qty > 1e-12) {
      const opens: "long" | "short" = pos === "LONG" ? "long" : pos === "SHORT" ? "short" : buy ? "long" : "short";
      book[opens].push({ qty, price: fillPrice(o), time: o.created_at, order: o });
    }
  }

  // What is left in the books is still open.
  for (const book of books.values()) {
    for (const side of ["long", "short"] as const) {
      for (const lot of book[side]) push(lot.order, side, lot.qty, lot, null);
    }
  }
  return trips.sort((a, b) => Date.parse(b.exitTime || b.entryTime || "") - Date.parse(a.exitTime || a.entryTime || ""));
}

/** The exchange's mark price of a position, else the one its entry and unrealized P&L imply. */
export function positionMarkPrice(p: Position): number | null {
  const mark = Number(p.mark_price);
  if (Number.isFinite(mark) && mark > 0) return mark;
  const entry = Number(p.entry_price);
  const size = Math.abs(Number(p.size));
  const upnl = Number(p.unrealized_pnl);
  if (!(entry > 0) || !(size > 0) || !Number.isFinite(upnl)) return null;
  return entry + (upnl / size) * (p.side === "SHORT" ? -1 : 1);
}

/**
 * An open trade's result at the position's current mark price on the same
 * account and symbol (the exchange drops a perpetual's ".P"); null when it has
 * no entry price or no such position. Without fees and funding.
 */
export function unrealizedTripPnl(
  t: RoundTrip,
  positions: Position[] | undefined
): { pnl: number; pnlPct: number } | null {
  if (!t.open || t.entryPrice == null || !positions?.length) return null;
  const sym = (s: string) => s.toUpperCase().replace(/\.P$/, "");
  const pos = positions.find((p) => sym(p.symbol) === sym(t.symbol));
  const mark = pos ? positionMarkPrice(pos) : null;
  if (mark == null) return null;
  const pnl = (mark - t.entryPrice) * t.qty * (t.side === "long" ? 1 : -1);
  return { pnl, pnlPct: t.entryPrice ? (pnl / (t.entryPrice * t.qty)) * 100 : 0 };
}

/**
 * Realized P&L of each order that closed (part of) a position, from the
 * entries it matched; null when a price is missing. Orders that only opened
 * are absent.
 */
export function realizedPnlByOrder(orders: OrderRecord[]): Map<string, number | null> {
  const out = new Map<string, number | null>();
  for (const t of matchRoundTrips(orders)) {
    if (!t.exitOrderId) continue;
    const prev = out.get(t.exitOrderId);
    out.set(t.exitOrderId, prev === null || t.pnl == null ? null : (prev ?? 0) + t.pnl);
  }
  return out;
}

/** What the trades closed since `since` (unix ms) realized: P&L without fees, and how many won. */
export function closedSince(orders: OrderRecord[], since: number): { pnl: number; closed: number; wins: number } {
  let pnl = 0;
  let closed = 0;
  let wins = 0;
  for (const t of matchRoundTrips(orders)) {
    if (t.open || t.pnl == null || !t.exitTime || !(Date.parse(t.exitTime) >= since)) continue;
    pnl += t.pnl;
    closed++;
    if (t.pnl > 0) wins++;
  }
  return { pnl, closed, wins };
}

export type RealPnlPoint = {
  /** Unix ms */
  t: number;
  /** Realized P&L so far, in quote currency */
  pnl: number;
};

/**
 * Realized P&L over time from orders filled on the exchange: one point per
 * closed trade, at its exit, each adding its result to the running total. It
 * starts at 0 when the first of them was opened. Without fees and funding, as
 * the trades themselves; trades whose entry wasn't loaded have no result and
 * are left out.
 */
export function realizedPnlSeries(orders: OrderRecord[]): RealPnlPoint[] {
  const closed = matchRoundTrips(orders)
    .filter((t) => !t.open && t.pnl != null && t.exitTime)
    .map((t) => ({ exit: Date.parse(t.exitTime!), entry: Date.parse(t.entryTime || ""), pnl: t.pnl! }))
    .filter((t) => Number.isFinite(t.exit))
    .sort((a, b) => a.exit - b.exit);
  if (!closed.length) return [];
  const starts = closed.map((t) => t.entry).filter(Number.isFinite);
  const out: RealPnlPoint[] = [{ t: Math.min(closed[0]!.exit, ...starts), pnl: 0 }];
  let total = 0;
  for (const t of closed) {
    total += t.pnl;
    const last = out[out.length - 1]!;
    // Several fills of one exit order are one step.
    if (last.t === t.exit && out.length > 1) last.pnl = total;
    else out.push({ t: t.exit, pnl: total });
  }
  return out;
}
